// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const SalesforceView = ({ requests = [], repo = [], onAdd = undefined, user = null }: { requests?: RequestRow[]; repo?: ContractRow[]; onAdd?: any; user?: UserRow | null }) => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("browse");
  const [selectedOpp, setSelectedOpp] = useState<any>(null);
  const [showLinkToRepo, setShowLinkToRepo] = useState<any>(null);

  const filtered = SF_OPPORTUNITIES.filter(o => `${o.name} ${o.accountName} ${o.owner} ${o.stage}`.toLowerCase().includes(search.toLowerCase()));

  const linkedRequests = (oppId: string) => requests.filter((r: RequestRow) => r.sfOppId === oppId);
  const linkedContracts = (accountName: string) => repo.filter((c: ContractRow) => c.counterparty?.includes(accountName.split(" ")[0]) || c.company?.includes(accountName.split(" ")[0]));

  const tabs = [
    { id: "browse", label: "Browse Opportunities", count: SF_OPPORTUNITIES.length },
    { id: "linked", label: "Linked Records", count: requests.filter((r: RequestRow) => r.sfOppId).length },
    { id: "sync", label: "Status Sync" },
  ];

  return (
    <div>
      <SectionTitle sub="Browse, import, and sync with Salesforce Opportunity records" right={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge c={T.grn} b={T.grnS} size="md">☁ Connected</Badge>
          <a href={SF_INSTANCE} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#0088D1", textDecoration: "none", fontWeight: 600 }}>Open Salesforce ↗</a>
        </div>
      }>Salesforce Integration</SectionTitle>

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "browse" && (
        <div>
          <SearchBar value={search} onChange={setSearch} placeholder="Search opportunities by name, account, owner, or stage..." />
          {filtered.map(opp => {
            const sc = SF_STAGE_COLORS[opp.stage] || { c: T.txM, b: T.bg4 };
            const linked = linkedRequests(opp.id);
            return (
              <Card key={opp.id} style={{ marginBottom: 8, borderLeft: `3px solid ${sc.c}` }} onClick={() => setSelectedOpp(selectedOpp?.id === opp.id ? null : opp)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{opp.name}</span>
                      <Badge c={sc.c} b={sc.b}>{opp.stage}</Badge>
                      {linked.length > 0 && <Badge c="#0088D1" b="rgba(0,136,209,.1)">{linked.length} linked</Badge>}
                    </div>
                    <div style={{ display: "flex", gap: 12, fontSize: 11, color: T.txM, flexWrap: "wrap" }}>
                      <span>{opp.accountName}</span>
                      <span style={{ color: T.txD }}>·</span>
                      <span>${(opp.amount || 0).toLocaleString()}</span>
                      <span style={{ color: T.txD }}>·</span>
                      <span>Close: {fmtDate(opp.closeDate)}</span>
                      <span style={{ color: T.txD }}>·</span>
                      <span>Owner: {opp.owner}</span>
                      <span style={{ color: T.txD }}>·</span>
                      <span>{opp.probability}%</span>
                      {opp.type && <><span style={{ color: T.txD }}>·</span><span>{opp.type}</span></>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                    <a href={sfLink(opp.id)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: "#0088D1", textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap" }}>SF ↗</a>
                  </div>
                </div>

                {/* Expanded Detail */}
                {selectedOpp?.id === opp.id && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.brd}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>
                      {[
                        ["Account", opp.accountName], ["Amount", `$${(opp.amount || 0).toLocaleString()}`],
                        ["Close Date", fmtDate(opp.closeDate)], ["Stage", opp.stage],
                        ["Owner", opp.owner], ["Probability", `${opp.probability}%`],
                        ["Contract Terms", opp.contractTerms || "N/A"], ["Payment Terms", opp.paymentTerms || "N/A"],
                        ["Renewal Date", fmtDate(opp.renewalDate) || "N/A"], ["Type", opp.type],
                        ...Object.entries(opp.customFields || {}).map(([k, v]) => [k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, " $1"), v]),
                      ].map(([l, v], i) => (
                        <div key={i}><div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 2 }}>{l}</div><div style={{ fontSize: 11, color: T.tx, fontWeight: 500 }}>{v}</div></div>
                      ))}
                    </div>

                    {/* Linked CLM Records */}
                    {linked.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.txM, marginBottom: 4 }}>LINKED CLM REQUESTS</div>
                        {linked.map((r: RequestRow) => {
                          const rsc = STATUS_COLORS[r.status || ''] || { c: "", b: "" };
                          return <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", backgroundColor: T.bg3, borderRadius: 4, marginBottom: 2 }}>
                            <span style={{ fontSize: 11, color: T.tx }}>{r.title}</span>
                            <Badge c={rsc.c} b={rsc.b} dot>{r.status}</Badge>
                          </div>;
                        })}
                      </div>
                    )}

                    {/* Related Executed Contracts */}
                    {linkedContracts(opp.accountName).length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.txM, marginBottom: 4 }}>RELATED EXECUTED CONTRACTS</div>
                        {linkedContracts(opp.accountName).map((c: any) => (
                          <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", backgroundColor: T.bg3, borderRadius: 4, marginBottom: 2 }}>
                            <span style={{ fontSize: 11, color: T.tx }}>{c.name}</span>
                            <Badge c={T.grn} b={T.grnS}>Executed</Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn v="primary" sz="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); /* would trigger intake with SF data */ }}>Create Contract Request</Btn>
                      <Btn v="secondary" sz="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}>Link to Existing Contract</Btn>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
          {filtered.length === 0 && <EmptyState icon="☁" msg="No matching opportunities" />}
        </div>
      )}

      {tab === "linked" && (
        <div>
          <div style={{ fontSize: 12, color: T.txM, marginBottom: 12 }}>Contract requests linked to Salesforce Opportunities</div>
          {requests.filter((r: RequestRow) => r.sfOppId).length === 0 ? <EmptyState icon="☁" msg="No linked records yet. Import from Salesforce when creating a new request." /> : (
            requests.filter((r: RequestRow) => r.sfOppId).map((r: RequestRow) => {
              const sc = STATUS_COLORS[r.status || ''] || { c: "", b: "" };
              const opp = SF_OPPORTUNITIES.find(o => o.id === r.sfOppId);
              return (
                <Card key={r.id} style={{ marginBottom: 6, borderLeft: `3px solid #0088D1` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{r.title}</span>
                        <Badge c={sc.c} b={sc.b} dot>{r.status}</Badge>
                      </div>
                      <div style={{ fontSize: 11, color: T.txM }}>{r.counterparty} · CLM: {r.status}</div>
                      {opp && <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>SF Stage: {opp.stage} · ${(opp.amount || 0).toLocaleString()}</div>}
                    </div>
                    <a href={sfLink(r.sfOppId || "")} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: "#0088D1", textDecoration: "none", fontWeight: 600 }}>Open in SF ↗</a>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {tab === "sync" && (
        <div>
          <div style={{ fontSize: 12, color: T.txM, marginBottom: 16 }}>Status synchronization between CLM contract status and Salesforce Opportunity stage. When a contract reaches "Fully Executed," the linked Opportunity is automatically updated to "Closed Won."</div>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Sync Mapping</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center" }}>
              {[
                ["Pending Assignment", "→", "Qualification"],
                ["Legal Review", "→", "Negotiation/Review"],
                ["Legal Review Completed", "→", "Negotiation/Review"],
                ["Approved for Signature", "→", "Negotiation/Review"],
                ["Fully Executed", "→", "Closed Won"],
                ["Cancelled", "→", "Closed Lost"],
              ].map(([clm, arrow, sf], i) => {
                const clmSc = STATUS_COLORS[clm] || { c: "", b: "" };
                const sfSc = SF_STAGE_COLORS[sf] || { c: "", b: "" };
                return [
                  <div key={`c${i}`} style={{ textAlign: "right" }}><Badge c={clmSc.c} b={clmSc.b} size="md" dot>{clm}</Badge></div>,
                  <div key={`a${i}`} style={{ textAlign: "center", color: T.txD, fontSize: 14 }}>{arrow}</div>,
                  <div key={`s${i}`}><Badge c={sfSc.c} b={sfSc.b} size="md">☁ {sf}</Badge></div>,
                ];
              }).flat()}
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Sync Configuration</div>
            {[
              ["Direction", "Bidirectional (CLM ↔ Salesforce)"],
              ["Auto-sync trigger", "On status change in CLM"],
              ["Conflict resolution", "CLM status takes precedence"],
              ["Sync frequency", "Real-time via Salesforce API webhooks"],
              ["API version", "Salesforce REST API v59.0"],
            ].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                <span style={{ fontSize: 11, color: T.txM }}>{l}</span>
                <span style={{ fontSize: 11, color: T.tx, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ CONTRACT TYPE MANAGER ═══════════════════ */
